import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContext, Controller } from 'react-hook-form';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { Typography } from '@mui/material';
import FormActions from '#component/project/form/form-actions';
import { fetchMeta } from '#action/meta/device';
import { toMeta } from '#helper/rhf-meta';
import {
  ButtonContainer,
  Cnt,
  StyledButton,
  StyledPaper
} from './styles';
import { StyledTextField, StyledDescriptionField } from './field-styles';
import ParentProjectSelectionField from './parent-project-selection-field';
import DateFields from './date-fields';
import DevicesUsersSelectionFields from './devices-users-selection-fields';
import ProjectWarning from './project-warning';
import ProjectModules from './project-modules';
import { useIntersection } from '#hooks';
import { flatTreeProjects } from '#helper';

const intersectionOptions = {
  rootMargin: '-69px',
  threshold: [1]
};

const emptyWarning = { endDateWarnProjects: [], unassignDeviceWarnProjects: [] };

/**
 * Renders project from.
 * @returns {React.FC}
 */

const ProjectForm = props => {
  const location = useLocation();
  const {
    history,
    match,
    submitting,
    editable,
    title,
    projects,
    devices = [],
    users,
    arrayProjects,
    minMaxDate,
    currentProject,
    nextStep,
    initialValues,
    forceError,
    formModules,
    submitForm,
    saveDisabled
  } = props;

  const { sentinelRef, isIntersecting } = useIntersection(intersectionOptions);
  const dispatch = useDispatch();
  const { control, watch, setValue, formState } = useFormContext();

  const [warning, setWarning] = useState(emptyWarning);
  const formData = watch();
  const meta = useSelector(s => s.meta, shallowEqual);

  const change = useCallback((name, value) => {
    setValue(name, value, { shouldDirty: true, shouldValidate: true });
  }, [setValue]);

  useEffect(() => {
    if (!Object.values(meta).some(v => v.length)) {
      dispatch(fetchMeta());
    }
  }, [dispatch, meta]);

  const handleProjectFormSubmit = event => {
    event.preventDefault();

    if (!currentProject) {
      submitForm(event);
      return;
    }

    const filterFn = project => {
      if (
        (Date.parse(formData.beginDate) && formData.beginDate > project.beginDate) ||
        (Date.parse(formData.expirationDate) && formData.expirationDate < project.expirationDate)
      ) {
        return true;
      }
      return false;
    };

    const childProjects = flatTreeProjects(currentProject.children);

    const endDateWarnProjects = childProjects.filter(filterFn);

    const unassignedDevices = (initialValues.devices ?? []).filter(
      initialValueDevice => !formData.devices?.find(
        formDataDevice => formDataDevice.value === initialValueDevice.value
      )
    );

    const projectDevicesMap = new Map();

    devices.forEach(({ id: deviceId, projectId }) => {
      const projectDevicesSet = projectDevicesMap.get(projectId);

      if (!projectDevicesSet) {
        projectDevicesMap.set(projectId, new Set([deviceId]));
      } else {
        projectDevicesSet.add(deviceId);
      }
    });

    const unassignDeviceWarnProjects = childProjects.reduce((acc, childProject) => {
      const childProjectDeviceIdSet = projectDevicesMap.get(childProject.id);
      const childProjectUnassignedDevices = unassignedDevices
        .filter(device => childProjectDeviceIdSet?.has(device.value));

      if (childProjectUnassignedDevices.length) {
        acc.push({ ...childProject, unassignedDevices: childProjectUnassignedDevices });
      }

      return acc;
    }, []);

    if (endDateWarnProjects.length || unassignDeviceWarnProjects.length) {
      setWarning({ endDateWarnProjects, unassignDeviceWarnProjects });
    } else {
      submitForm(event);
    }
  };

  const onDialogCancel = () => {
    setWarning(emptyWarning);
  };

  const onDialogAccept = () => {
    submitForm();
    setWarning(emptyWarning);
  };

  const handleCancelClick = () => {
    if (location.key) {
      history.goBack();
    } else {
      history.push('/project');
    }
  };

  return (
    <Cnt>
      {!editable ? (
        <FormActions projectTitle={initialValues.title}>
          <Typography variant="h4">
            {title}
          </Typography>
        </FormActions>
      ) : (
        <Typography variant="h4" gutterBottom>
          {title}
        </Typography>
      )}
      <form onSubmit={handleProjectFormSubmit} autoComplete="off">
        <StyledPaper elevation={0}>
          <ParentProjectSelectionField
            projects={projects}
            arrayProjects={arrayProjects}
            formData={formData}
            change={change}
            editable={editable}
          />
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <StyledTextField
                {...field}
                meta={toMeta(fieldState, formState)}
                label="Project name"
                variant="outlined"
                fullWidth
                forceError={forceError}
              />
            )}
          />
          <DateFields
            minMaxDate={minMaxDate}
            formData={formData}
            change={change}
            initialValues={initialValues}
            forceError={forceError}
          />
          <DevicesUsersSelectionFields
            formData={formData}
            devices={devices}
            projects={projects}
            users={users}
            change={change}
            currentProject={currentProject}
            initialDevices={initialValues.devices}
            disabled={initialValues.disableFieldsInRoot}
            forceError={forceError}
          />
          <ProjectModules
            match={match}
            formModules={formModules}
            currentProject={currentProject}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <StyledDescriptionField
                {...field}
                value={field.value ?? ''}
                meta={toMeta(fieldState, formState)}
                label="Project description"
                variant="outlined"
                minRows={4}
                forceError={forceError}
              />
            )}
          />
          <ProjectWarning
            endDateWarnProjects={warning.endDateWarnProjects}
            unassignDeviceWarnProjects={warning.unassignDeviceWarnProjects}
            onCancel={onDialogCancel}
            onAccept={onDialogAccept}
          />
        </StyledPaper>
        <div ref={sentinelRef} />
        <ButtonContainer isIntersecting={isIntersecting}>
          <StyledButton variant="outlined" onClick={handleCancelClick}>
            Cancel
          </StyledButton>
          <StyledButton
            disabled={submitting}
            onClick={nextStep}
            variant="outlined"
          >
            Next
          </StyledButton>
          <StyledButton
            disabled={saveDisabled}
            type="submit"
            variant="contained"
            color="secondary"
          >
            Save
          </StyledButton>
        </ButtonContainer>
      </form>
    </Cnt>
  );
};

export default ProjectForm;
